import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  MessageSquare, 
  Mail, 
  Phone, 
  Copy, 
  ExternalLink,
  Building2,
  Clock,
  User,
  FileJson,
  Trash2,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from './shared';
import { formatRelativeDate } from '@/lib/utils';

interface ContactSubmission {
  id: string;
  inquiry_type: string;
  name: string;
  email: string;
  phone: string | null;
  organization: string | null;
  subject: string | null;
  category: string | null;
  custom_category: string | null;
  partner_type: string | null;
  urgency: string | null;
  example_json: string | null;
  message: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

type StatusFilter = 'new' | 'in_progress' | 'resolved' | 'archived';

import { 
  CONTACT_STATUS_OPTIONS, 
  INQUIRY_TYPE_LABELS 
} from '@/lib/admin/constants';

export function ContactSubmissionsManager() {
  const { toast } = useToast();
  
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<StatusFilter>('new');

  // Sheet state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<ContactSubmission | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter submissions by status
  const newSubmissions = submissions.filter(s => s.status === 'new' || s.status === 'read');
  const inProgressSubmissions = submissions.filter(s => s.status === 'in_progress');
  const resolvedSubmissions = submissions.filter(s => s.status === 'resolved');
  const archivedSubmissions = submissions.filter(s => s.status === 'archived');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('contact_submissions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({ title: 'Error fetching submissions', description: error.message, variant: 'destructive' });
    } else if (data) {
      setSubmissions(data);
    }
    setLoading(false);
  };

  const openSubmission = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setEditStatus(submission.status);
    setEditNotes(submission.admin_notes || '');
    setSheetOpen(true);
    
    // Mark as read if new
    if (submission.status === 'new') {
      markAsRead(submission.id);
    }
  };

  const markAsRead = async (id: string) => {
    await supabase
      .from('contact_submissions')
      .update({ status: 'read' })
      .eq('id', id);
    
    setSubmissions(prev => prev.map(s => 
      s.id === id ? { ...s, status: 'read' } : s
    ));
  };

  const handleSave = async () => {
    if (!selectedSubmission) return;

    setIsSaving(true);
    const { error } = await supabase
      .from('contact_submissions')
      .update({
        status: editStatus,
        admin_notes: editNotes || null,
      })
      .eq('id', selectedSubmission.id);

    if (error) {
      toast({ title: 'Error updating submission', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Submission updated' });
      setSheetOpen(false);
      fetchData();
    }
    setIsSaving(false);
  };

  const confirmDelete = (submission: ContactSubmission) => {
    setSubmissionToDelete(submission);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!submissionToDelete) return;
    
    setIsDeleting(true);
    const { error } = await supabase
      .from('contact_submissions')
      .delete()
      .eq('id', submissionToDelete.id);
    
    if (error) {
      toast({ title: 'Error deleting submission', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Submission deleted' });
      fetchData();
    }
    
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setSubmissionToDelete(null);
  };

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast({ title: `${label} copied to clipboard` });
  };

  const getInquiryBadgeVariant = (type: string) => {
    switch (type) {
      case 'support': return 'destructive';
      case 'partner': return 'default';
      case 'proposal': return 'secondary';
      case 'feature': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-500 hover:bg-blue-600">New</Badge>;
      case 'read':
        return <Badge variant="outline">Read</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600 text-black">In Progress</Badge>;
      case 'resolved':
        return <Badge className="bg-green-500 hover:bg-green-600">Resolved</Badge>;
      case 'archived':
        return <Badge variant="secondary">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const renderSubmissionsTable = (submissionsList: ContactSubmission[]) => (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-24">Type</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead className="w-32">Received</TableHead>
            <TableHead className="w-28">Status</TableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissionsList.map((submission) => (
            <TableRow
              key={submission.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => openSubmission(submission)}
            >
              <TableCell>
                <Badge variant={getInquiryBadgeVariant(submission.inquiry_type)}>
                  {INQUIRY_TYPE_LABELS[submission.inquiry_type] || submission.inquiry_type}
                </Badge>
              </TableCell>
              <TableCell>
                <span className="font-medium">{submission.name}</span>
              </TableCell>
              <TableCell>
                <span className="text-muted-foreground">{submission.email}</span>
              </TableCell>
              <TableCell>
                <span className="truncate max-w-[200px] block">
                  {submission.subject || submission.organization || '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className="text-muted-foreground text-sm">
                  {formatRelativeDate(submission.created_at)}
                </span>
              </TableCell>
              <TableCell>
                {getStatusBadge(submission.status)}
              </TableCell>
              <TableCell className="text-right">
                <div 
                  className="flex items-center justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => confirmDelete(submission)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderEmptyState = (tabName: string) => (
    <AdminEmptyState
      icon={MessageSquare}
      title={`No ${tabName} submissions`}
      description={`Submissions that are ${tabName.toLowerCase()} will appear here.`}
    />
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Contact Submissions"
        count={submissions.length}
      />

      {loading ? (
        <AdminTableSkeleton columns={7} rows={5} />
      ) : submissions.length === 0 ? (
        <AdminEmptyState
          icon={MessageSquare}
          title="No submissions yet"
          description="Contact form submissions will appear here when users submit the form."
        />
      ) : (
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as StatusFilter)}>
          <TabsList>
            <TabsTrigger value="new">
              New ({newSubmissions.length})
            </TabsTrigger>
            <TabsTrigger value="in_progress">
              In Progress ({inProgressSubmissions.length})
            </TabsTrigger>
            <TabsTrigger value="resolved">
              Resolved ({resolvedSubmissions.length})
            </TabsTrigger>
            <TabsTrigger value="archived">
              Archived ({archivedSubmissions.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="new">
            {newSubmissions.length === 0 
              ? renderEmptyState('new')
              : renderSubmissionsTable(newSubmissions)
            }
          </TabsContent>
          
          <TabsContent value="in_progress">
            {inProgressSubmissions.length === 0 
              ? renderEmptyState('in progress')
              : renderSubmissionsTable(inProgressSubmissions)
            }
          </TabsContent>
          
          <TabsContent value="resolved">
            {resolvedSubmissions.length === 0 
              ? renderEmptyState('resolved')
              : renderSubmissionsTable(resolvedSubmissions)
            }
          </TabsContent>
          
          <TabsContent value="archived">
            {archivedSubmissions.length === 0 
              ? renderEmptyState('archived')
              : renderSubmissionsTable(archivedSubmissions)
            }
          </TabsContent>
        </Tabs>
      )}

      {/* Submission Detail Sheet */}
      <AdminSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title={selectedSubmission ? `${INQUIRY_TYPE_LABELS[selectedSubmission.inquiry_type] || 'Contact'} from ${selectedSubmission.name}` : 'Submission Details'}
        description={selectedSubmission ? `Submitted ${formatRelativeDate(selectedSubmission.created_at)}` : ''}
        onSave={handleSave}
        isSaving={isSaving}
        saveLabel="Save Changes"
        size="xl"
      >
        {selectedSubmission && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-medium">Quick Actions</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                >
                  <a href={`mailto:${selectedSubmission.email}`}>
                    <Mail className="h-4 w-4 mr-2" />
                    Email {selectedSubmission.name.split(' ')[0]}
                  </a>
                </Button>
                {selectedSubmission.phone && (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={`tel:${selectedSubmission.phone}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      Call
                    </a>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(selectedSubmission.email, 'Email')}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(selectedSubmission.message, 'Message')}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Message
                </Button>
                {selectedSubmission.example_json && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(selectedSubmission.example_json!, 'JSON')}
                  >
                    <FileJson className="h-4 w-4 mr-2" />
                    Copy JSON
                  </Button>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Info Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Contact Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <p className="font-medium">{selectedSubmission.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <p className="font-medium">{selectedSubmission.email}</p>
                </div>
                {selectedSubmission.organization && (
                  <div>
                    <span className="text-muted-foreground">Organization:</span>
                    <p className="font-medium">{selectedSubmission.organization}</p>
                  </div>
                )}
                {selectedSubmission.phone && (
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="font-medium">{selectedSubmission.phone}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Inquiry Details Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Inquiry Details
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Type:</span>
                  <p className="font-medium">{INQUIRY_TYPE_LABELS[selectedSubmission.inquiry_type] || selectedSubmission.inquiry_type}</p>
                </div>
                {selectedSubmission.subject && (
                  <div>
                    <span className="text-muted-foreground">Subject:</span>
                    <p className="font-medium">{selectedSubmission.subject}</p>
                  </div>
                )}
                {selectedSubmission.category && (
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <p className="font-medium">
                      {selectedSubmission.category}
                      {selectedSubmission.custom_category && ` (${selectedSubmission.custom_category})`}
                    </p>
                  </div>
                )}
                {selectedSubmission.partner_type && (
                  <div>
                    <span className="text-muted-foreground">Partner Type:</span>
                    <p className="font-medium">{selectedSubmission.partner_type}</p>
                  </div>
                )}
                {selectedSubmission.urgency && (
                  <div>
                    <span className="text-muted-foreground">Urgency:</span>
                    <p className="font-medium capitalize">{selectedSubmission.urgency}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Message Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-muted-foreground">Message</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(selectedSubmission.message, 'Message')}
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="bg-muted/30 rounded-lg p-4 text-sm whitespace-pre-wrap">
                {selectedSubmission.message}
              </div>
            </div>

            {/* Example JSON Section (for proposals) */}
            {selectedSubmission.example_json && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <FileJson className="h-4 w-4" />
                    Example JSON
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedSubmission.example_json!, 'JSON')}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <pre className="bg-muted/30 rounded-lg p-4 text-sm overflow-x-auto font-mono">
                  {selectedSubmission.example_json}
                </pre>
              </div>
            )}

            {/* Admin Notes Section */}
            <div className="space-y-2">
              <Label htmlFor="admin_notes">Admin Notes (internal only)</Label>
              <Textarea
                id="admin_notes"
                placeholder="Add internal notes about this submission..."
                rows={4}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>

            {/* Metadata */}
            <div className="text-xs text-muted-foreground flex items-center gap-4 pt-4 border-t">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Created: {new Date(selectedSubmission.created_at).toLocaleString()}
              </span>
              {selectedSubmission.updated_at !== selectedSubmission.created_at && (
                <span>
                  Updated: {new Date(selectedSubmission.updated_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemType="submission"
        itemName={submissionToDelete?.name}
      />
    </div>
  );
}
